/* CustomFieldsComponent.js */

"use strict";

const EventEmitter = require("events").EventEmitter;
const Renderer = require("./CustomFieldsRenderer");
const _ = require("lodash");
const Uuid = require("uuid");
const {parseLocalDate, toUTCMidDayTimestamp} = require("@gluenetworks/date_cell_editor").localDate;

const TYPE_CHANGE_ERROR_MSG = (name) => `Custom field "${name}": Data type cannot be updated`;
const NAME_DUPLICATE_ERROR_MSG = (name) => `Custom field "${name}": Field name must be unique`;
const ENTITY_ERROR_MSG = (name, msg) => `Custom field "${name}": ${msg}`;

const MAX_TAG_NAME_LENGTH = 32;
const TAG_NAME_CHARSET_PATTERN = /^[a-zA-Z0-9 .\-_&@:()[\]]*$/;

const NO_TAG_NAME_ERROR_MSG = "All tags must have a name";
const TAG_NAME_DUPLICATE_ERROR_MSG = (name) => `Tag "${name}": Tag name must be unique`;
const TAG_NAME_LENGTH_ERROR_MSG = (name) =>
    `Tag "${name}": Tag name must be 32 characters or fewer`;
const TAG_NAME_CHARSET_ERROR_MSG = (name) =>
    `The Tag name "${name}" can only contain letters, numbers, spaces, ".", "-", "_", "&", "@", ":", "(", ")", "[", "]"`;
const TAG_AUTO_DELETE_INVALID_MSG = (name) => `Tag "${name}": Expiration must be a valid date`;
const TAG_AUTO_DELETE_FUTURE_MSG = (name) =>
    `Tag "${name}": Expiration must be today or a future date`;
const TAG_ENTITY_ERROR_MSG = (name, msg) => `Tag "${name}": ${msg}`;

/**
 * Convert a local-date string into a Unix timestamp at 12:00 PM UTC
 * on that calendar date. Returns undefined for an empty/unset value.
 * @param {string} value
 * @returns {number|undefined}
 */
function toAutoDeleteTimestamp(value) {
    if (!value) {
        return undefined;
    }
    const parsed = parseLocalDate(value);
    return parsed ? toUTCMidDayTimestamp(parsed) : undefined;
}

/**
 * class CustomFieldsComponent
 *
 * @fires CustomFieldsComponent#DataUpdated
 * @fires CustomFieldsComponent#TagsDataUpdated
 */
class CustomFieldsComponent extends EventEmitter {
    /**
     *
     * @param {CustomFieldsEntity.} CustomFieldsEntity
     * @param {TagsEntity.} TagsEntity
     * @param {UserSession} userSession
     * @param {UiUtils} uiUtils
     * @param {CoreLogger} logger
     */
    constructor(CustomFieldsEntity, TagsEntity, userSession, uiUtils, logger, userFeedback) {
        super();

        this._CustomFieldsEntity = CustomFieldsEntity;
        this._TagsEntity = TagsEntity;
        this._userSession = userSession;
        this._user = userSession.getUser();
        this._logger = logger;
        this._muf = userFeedback;

        /**
         *
         * @type {CustomFieldsEntity|undefined}
         * @private
         */
        this._customFieldsEntity = undefined;

        /**
         * @typedef {Object<string, Object>} ModelData - origin data stored in the model
         *
         * @type {ModelData}
         * @private
         */
        this._data = {};

        /**
         * Loaded TagsEntity instances, keyed by their real (server-assigned) id.
         * @type {Object<string, TagsEntity>}
         * @private
         */
        this._tagsData = {};

        /**
         *
         * @type {?{inherited: string[], editable: string[], systemOwned: string[]}};
         * @private
         */
        this._fieldsKeys = {inherited: [], editable: [], systemOwned: []};

        this.saveProgress = this.saveProgress.bind(this);

        this._renderer = new Renderer(this, uiUtils);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _triggerDataUpdated() {
        /**
         * @event CustomFieldsComponent#DataUpdated
         */
        this.emit("DataUpdated");
    }

    _triggerTagsDataUpdated() {
        /**
         * @event CustomFieldsComponent#TagsDataUpdated
         */
        this.emit("TagsDataUpdated");
    }

    _registerListeners() {}

    _unregisterListeners() {}

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();

        if (this._customFieldsEntity && this._customFieldsEntity.shutdown) {
            this._customFieldsEntity.shutdown().catch((error) => this._logger.warn({error}));
        }

        return this._renderer.shutdown().then(
            () => {
                this._CustomFieldsEntity = null;
                this._TagsEntity = null;
                this._userSession = null;
                this._user = null;
                this._logger = null;
                this._customFieldsEntity = null;
                this._data = null;
                this._tagsData = null;
                this._fieldsKeys = null;
                this._renderer = null;
            },
            (err) => {
                console.log("Error shutting down CustomFieldsComponent: ", err);
            }
        );
    }

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    /**
     * Gates editing (not visibility) of both the Custom Fields and Tags tables.
     */
    get canManage() {
        return Boolean(this._user.isAuthorizedForAction("customFieldsWrite"));
    }

    get activeOrgId() {
        return this.getActiveOrgId();
    }

    getActiveOrgId() {
        return _.get(this._userSession, "activeOrganization.id");
    }

    get data() {
        return this.getData();
    }

    getData() {
        return this._data;
    }

    get tagsData() {
        return this._tagsData;
    }

    get keys() {
        return this.getFieldsKeys();
    }

    get CustomFieldsEntity() {
        return this._CustomFieldsEntity;
    }

    getFieldsKeys(section) {
        return section ? this._fieldsKeys[section] || [] : this._fieldsKeys;
    }

    setCustomFieldsEntity(newOrgInstance) {
        this._customFieldsEntity = newOrgInstance;
        return this._customFieldsEntity;
    }

    setData(newData) {
        this._data = newData;
    }

    setTagsData(newData) {
        this._tagsData = newData;
    }

    setKeys(keysObj) {
        this._fieldsKeys = keysObj;
    }

    retrieveCustomFieldsEntity(tenantId) {
        if (this._customFieldsEntity && this._customFieldsEntity.shutdown) {
            this._customFieldsEntity.shutdown().catch((error) => this._logger.warn({error}));
        }
        return this._CustomFieldsEntity
            .retrieveCustomFields(tenantId)
            .then((customFieldsEntity) => this.setCustomFieldsEntity(customFieldsEntity));
    }

    getCustomFieldsData() {
        this.setData({});
        this.setKeys({inherited: [], editable: [], systemOwned: []});
        if (!this.activeOrgId) {
            return Promise.resolve();
        }
        return this.retrieveCustomFieldsEntity(this.activeOrgId)
            .then((customFieldsEntity) => {
                this.updateDataAndKeys(customFieldsEntity);
                return customFieldsEntity.loadInheritedFields();
            })
            .then((inheritedCustomFields) => {
                this.setData(Object.assign({}, this._data, inheritedCustomFields));
                this.setKeys(
                    Object.assign({}, this._fieldsKeys, {
                        inherited: _.keys(inheritedCustomFields),
                    })
                );
            });
    }

    getTagsData() {
        this.setTagsData({});
        if (!this.activeOrgId) {
            return Promise.resolve();
        }
        return this._TagsEntity.loadTags(this.activeOrgId, {}).then((tagsEntities) => {
            this.setTagsData(_.keyBy(tagsEntities, "id"));
        });
    }

    updateDataAndKeys(customFieldsEntity = this._customFieldsEntity) {
        const entityFields = customFieldsEntity.getFields() || {};
        const {systemOwned, editable} = Object.entries(entityFields).reduce(
            (acc, [key, fieldData]) => {
                const section = fieldData.systemOwned ? "systemOwned" : "editable";
                return Object.assign(acc, {[section]: acc[section].concat(key)});
            },
            {systemOwned: [], editable: []}
        );

        this.setData(_.assign({}, this._data, entityFields));
        this.setKeys(_.assign({}, this._fieldsKeys, {systemOwned, editable}));
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    async whenModelIsReady() {
        try {
            await Promise.all([this.getCustomFieldsData(), this.getTagsData()]);
            this._registerListeners();
        } catch (err) {
            this._logger.warn(err, "CustomFieldsComponent.whenModelIsReady() error.");
            throw err;
        }
    }

    organizationChanged(/*orgObj*/) {
        this.getCustomFieldsData().then(() => this._triggerDataUpdated());
        this.getTagsData()
            .then(() => this._triggerTagsDataUpdated())
            .catch((error) => this._logger.warn({error}));
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    /**
     *
     * @returns {{dataType: string, name: string, id: string, enabled: boolean}}
     *
     */
    createCustomFieldItem() {
        return {
            dataType: "String",
            name: "New Field",
            id: Uuid.v4(),
            enabled: true,
            isNew: true,
            deviceOnly: true,
        };
    }

    /**
     *
     * @returns {{name: string, id: string}}
     */
    createTagItem() {
        return {
            name: "New Tag",
            id: Uuid.v4(),
            isNew: true,
        };
    }

    /**
     *
     * @param {ModelData} data
     * @returns {Promise<CustomFieldsEntity | Error>}
     */
    updateFields(data) {
        const customFieldFields = Object.assign(
            {},
            data,
            _.pick(this._data, this._fieldsKeys.systemOwned)
        );

        return this.validateData(customFieldFields)
            .then(() => this._customFieldsEntity.setFields(customFieldFields))
            .then(() => this.saveFields())
            .then((customFieldsEntity) => {
                this.updateDataAndKeys(customFieldsEntity);

                return customFieldsEntity;
            })
            .catch((errors) => {
                this._muf.hideDisplayed();
                return Promise.reject(errors);
            });
    }

    /**
     * Create/update each row individually - Tags has no bulk fields-bag endpoint.
     * @param {Array<{id: string, name: string, description: string, autoDeleteAt: number}>} rows - current SlickGrid rows
     * @returns {Promise<void | Array<string>>}
     */
    updateTags(rows) {
        return this.validateTagsData(rows).then(() => {
            this._muf.displayLoading({message: "Updating Tags...", showHideButton: true});
            return Promise.all(rows.map((row) => this._persistTagRow(row)))
                .then((failures) => {
                    this._muf.hideDisplayed();
                    const realFailures = failures.filter(Boolean);
                    if (realFailures.length) {
                        return Promise.reject(
                            realFailures.map(({name, error}) =>
                                TAG_ENTITY_ERROR_MSG(
                                    name,
                                    _.get(error, "errorMsg", "Unable to save tag")
                                )
                            )
                        );
                    }
                    return this.getTagsData();
                })
                .catch((errors) => {
                    this._muf.hideDisplayed();
                    return Promise.reject(errors);
                });
        });
    }

    /**
     * Create or update a single tag row against its TagsEntity instance.
     * @param {{id: string, name: string, description: string, autoDeleteAt: number}} row
     * @returns {Promise<?{name: string, error: Object}>} - resolves to a failure descriptor, or undefined on success
     * @private
     */
    _persistTagRow(row) {
        const existingEntity = this._tagsData[row.id];
        const entity = existingEntity || new this._TagsEntity(this.activeOrgId);

        entity.name = row.name;
        entity.description = row.description;
        entity.autoDeleteAt = toAutoDeleteTimestamp(row.autoDeleteAt);

        const persist = existingEntity ? () => entity.update() : () => entity.create();

        if (existingEntity && !entity.isDirty()) {
            return Promise.resolve();
        }

        return persist()
            .then(() => undefined)
            .catch((error) =>
                _.get(error, "errorCode") === "NoPendingUpdates"
                    ? undefined
                    : {name: row.name, error}
            );
    }

    /**
     *
     * @param {string} id - custom field id
     * @returns {Promise<CustomFieldsEntity | Error >}
     */
    deleteField(id) {
        if (!this._data[id]) {
            return Promise.resolve(this._customFieldsEntity);
        } else {
            const userEditableKeys = _.without(this._fieldsKeys.editable, id);
            return this.updateFields(_.pick(this.data, userEditableKeys)).then((/*response*/) => {
                //update data
                this.setData(_.omit(this._data, id));
                this.setKeys(Object.assign({}, this._fieldsKeys, {editable: userEditableKeys}));
            });
        }
    }

    /**
     *
     * @param {string} id - tag id
     * @returns {Promise<void | Error>}
     */
    deleteTag(id) {
        const entity = this._tagsData[id];
        if (!entity) {
            return Promise.resolve();
        }
        return entity.delete().then(() => {
            this.setTagsData(_.omit(this._tagsData, id));
        });
    }

    /**
     *
     * @returns {Promise<CustomFieldsEntity | Error>}
     */
    saveFields() {
        const options = {
            progressCb: this.saveProgress,
        };
        this.saveProgress({}, false);
        return this._customFieldsEntity.update(options);
    }

    saveProgress(prog, update = true) {
        if (!update) {
            return this._muf.displayLoading({
                message: "Updating Custom Fields...",
                showHideButton: true,
            });
        }
        if (prog.progressType && prog.progressType.toLowerCase() === "bulkop") {
            const msg = `${Math.floor((prog.progressCount / prog.totalCount) * 100)}%`;
            return this._muf.showLoadingProgress(msg);
        }
        if (prog.progressType && prog.progressType.toLowerCase() === "complete") {
            return this._muf.hideDisplayed();
        }
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /* START DATA VALIDATION MEtHODS           */
    /*******************************************/

    /**
     * validate data though validation functions
     * @param {Object<string, Object>} userData - current SlickGrid data
     * @returns {Promise<?string[]>} - list of error messages
     */
    validateData(userData) {
        const modelData = this._data;
        const validations = [
            this._entityValidation,
            this._validateDataTypeChange,
            this._validateNameDuplicates,
        ];

        const errors = validations
            .reduce(
                (errors, validation) => errors.concat(validation.call(this, modelData, userData)),
                []
            )
            .filter((msg, idx, list) => idx === list.indexOf(msg));

        return errors.length ? Promise.reject(errors) : Promise.resolve(null);
    }

    /**
     * validate tags data though validation functions
     * @param {TagsModelData} userData - current SlickGrid data
     * @returns {Promise<?string[]>} - list of error messages
     */
    validateTagsData(userData) {
        const validations = [
            this._validateRequiredTagName,
            this._validateTagNameLength,
            this._validateTagNameCharset,
            this._validateTagNameDuplicates,
            this._validateAutoDeleteDate,
        ];

        const errors = validations
            .reduce((errors, validation) => errors.concat(validation.call(this, userData)), [])
            .filter((msg, idx, list) => idx === list.indexOf(msg));

        return errors.length ? Promise.reject(errors) : Promise.resolve(null);
    }

    /**
     * data validation from CustomFieldsEntity
     * @param {ModelData} modelData - origin data stored in the model
     * @param {Object<string, Object>} userData - current SlickGrid data
     * @returns {Array<String>} - list of error messages
     * @private
     */
    _entityValidation(modelData, userData) {
        const errors = this._customFieldsEntity.validateFields(userData);
        return errors.map(({id, msg}) => ENTITY_ERROR_MSG(userData[id].name, msg));
    }

    /**
     * Check if the dataType column was changed
     * @param {ModelData} modelData - origin data stored in the model
     * @param {Object<string, Object>} userData - current SlickGrid data
     * @returns {Array<String>} - list of error messages
     * @private
     */
    _validateDataTypeChange(modelData, userData) {
        const changedItems = _.filter(
            userData,
            (item, id) => modelData[id] && modelData[id].dataType !== item.dataType
        );
        return changedItems.map((item) => TYPE_CHANGE_ERROR_MSG(item.name));
    }

    /**
     * Check if names(include inherited fields) have duplicates
     * @param {ModelData} modelData - origin data stored in the model
     * @param {Object<string, Object>} userData - current SlickGrid data
     * @returns {Array<String>} - list of error messages
     * @private
     */
    _validateNameDuplicates(modelData, userData) {
        return _.map(userData, _.property("name"))
            .reduce((duplicates, name, idx, arr) => {
                const isDuplicate = arr.lastIndexOf(name) !== idx;
                return isDuplicate && !duplicates.includes(name)
                    ? duplicates.concat(name)
                    : duplicates;
            }, [])
            .map(NAME_DUPLICATE_ERROR_MSG);
    }

    /**
     * Check that every tag has a non-empty name
     * @param {TagsModelData} userData - current SlickGrid data
     * @returns {Array<String>} - list of error messages
     * @private
     */
    _validateRequiredTagName(userData) {
        const hasMissingName = _.some(userData, (item) => !String(item.name || "").trim());
        return hasMissingName ? [NO_TAG_NAME_ERROR_MSG] : [];
    }

    /**
     * Check that no tag name exceeds the max length
     * @param {TagsModelData} userData - current SlickGrid data
     * @returns {Array<String>} - list of error messages
     * @private
     */
    _validateTagNameLength(userData) {
        return _.filter(userData, (item) => (item.name || "").length > MAX_TAG_NAME_LENGTH).map(
            (item) => TAG_NAME_LENGTH_ERROR_MSG(item.name)
        );
    }

    /**
     * Check that tag names only use the allowed character set
     * @param {TagsModelData} userData - current SlickGrid data
     * @returns {Array<String>} - list of error messages
     * @private
     */
    _validateTagNameCharset(userData) {
        return _.filter(
            userData,
            (item) => item.name && !TAG_NAME_CHARSET_PATTERN.test(item.name)
        ).map((item) => TAG_NAME_CHARSET_ERROR_MSG(item.name));
    }

    /**
     * Check if tag names have duplicates
     * @param {TagsModelData} userData - current SlickGrid data
     * @returns {Array<String>} - list of error messages
     * @private
     */
    _validateTagNameDuplicates(userData) {
        return _.map(userData, _.property("name"))
            .reduce((duplicates, name, idx, arr) => {
                const isDuplicate = arr.lastIndexOf(name) !== idx;
                return isDuplicate && !duplicates.includes(name)
                    ? duplicates.concat(name)
                    : duplicates;
            }, [])
            .map(TAG_NAME_DUPLICATE_ERROR_MSG);
    }

    /**
     * Expiration is optional. When provided it must be a parseable calendar date
     * ("must be a valid date"), and that date must be today or later
     * ("must be today or a future date").
     * @param {TagsModelData} userData - current SlickGrid data
     * @returns {Array<String>} - list of error messages
     * @private
     */
    _validateAutoDeleteDate(userData) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return _.reduce(
            userData,
            (errors, item) => {
                if (!item.autoDeleteAt) {
                    return errors; // optional - empty is fine
                }
                const date = parseLocalDate(item.autoDeleteAt);
                if (!date) {
                    return errors.concat(TAG_AUTO_DELETE_INVALID_MSG(item.name)); // unparseable
                }
                if (date.getTime() < today.getTime()) {
                    return errors.concat(TAG_AUTO_DELETE_FUTURE_MSG(item.name)); // past (today OK)
                }
                return errors;
            },
            []
        );
    }

    /*******************************************/
    /* START DATA VALIDATION METHODS           */
    /*******************************************/
}

module.exports = CustomFieldsComponent;

CustomFieldsComponent.$groupName = "Organization";

CustomFieldsComponent.$displayName = "Custom Fields";
CustomFieldsComponent.$license = ["GLUWARE"];

CustomFieldsComponent.$inject = [
    "$teve.entities.CustomFields",
    "$teve.entities.Tags",
    "$teve.userSession",
    "$teve.ui.UiUtils",
    "$teve.logger",
    "$teve.ui.userFeedback",
];
